// Shared pieces of the customer app: the app context, icons, the logo, sheets, the code input, the demo
// inbox, the document crop and the loading and error states.
import { createContext, type ComponentChildren, type CSSProperties, type JSX } from 'preact'
import { useContext, useEffect, useRef, useState } from 'preact/hooks'
import type { Dashboard, DemoMessage, FlowView, Me, Spot } from '../../../packages/contract/index.ts'
import { INSURER, SUPPORT_EMAIL, fill, type ErrKey, type Lang, type Strings } from './i18n.ts'
import { LOGO_WORD } from './logo.ts'
import { ApiError, NetworkError, api, codeIn } from './lib.ts'

// ───────────── app context ─────────────

export type NavOptions = { readonly replace?: boolean; readonly back?: boolean }
export type Problem = { readonly key: ErrKey; readonly until?: string }

export type App = {
  readonly t: Strings
  readonly lang: Lang
  readonly setLang: (lang: Lang) => void
  readonly theme: 'light' | 'dark'
  readonly setTheme: (theme: 'light' | 'dark') => void
  readonly path: string
  readonly dir: 1 | -1
  readonly nav: (path: string, options?: NavOptions) => void
  readonly goBack: (fallback: string) => void
  readonly me: Me | null
  readonly setMe: (me: Me | null) => void
  readonly flow: FlowView | null
  readonly setFlow: (flow: FlowView | null) => void
  readonly dash: Dashboard | null
  readonly setDash: (dash: Dashboard) => void
  readonly online: boolean
  readonly toast: (message: string) => void
  readonly sheet: (node: ComponentChildren | null) => void
  readonly problem: Problem | null
  readonly showProblem: (problem: Problem) => void
  /** Where onboarding goes next for this customer, worked out from Me (and the steps they skipped). */
  readonly nextStep: (me: Me) => Promise<string>
  readonly skip: (step: string) => void
  readonly unskip: (step: string) => void
  /** Where the consent screen returns after "Acepto", when it was opened from Settings. */
  readonly afterConsent: { current: string | null }
  readonly signOut: (why: 'manual' | 'idle' | 'all') => Promise<void>
  /** The uploaded lab document and the server's reading of it, kept in memory only. */
  readonly lab: { current: { readonly file: File; readonly draft: import('../../../packages/contract/index.ts').LabDraft } | null }
}

export const Ctx = createContext<App | null>(null)
export const useApp = (): App => {
  const app = useContext(Ctx)
  if (!app) throw new Error('useApp outside the app')
  return app
}

// ───────────── icons ─────────────

const P: Record<string, JSX.Element> = {
  home: <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />,
  summary: <path d="M5 20v-8M11 20V5M17 20v-5M3 20h18" />,
  rewards: <><circle cx="12" cy="9" r="6" /><path d="M8.5 14 7 22l5-3 5 3-1.5-8" /></>,
  policy: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></>,
  settings: <><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></>,
  back: <path d="M15 5l-7 7 7 7" />,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />,
  offline: <path d="M3 3l18 18M8.5 16.5a5 5 0 0 1 7 0M5 13a10 10 0 0 1 5.2-2.8M19 13a10 10 0 0 0-2.4-1.8M2 9.5a15 15 0 0 1 4.3-2.7M12 20h.01" />,
  retry: <path d="M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4" />,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  eyeOff: <path d="M3 3l18 18M10.6 6.1A10 10 0 0 1 12 6c5 0 9 6 9 6a17 17 0 0 1-2.6 3.2M6.6 7.6A17 17 0 0 0 3 12s4 6 9 6a9 9 0 0 0 4.4-1.2" />,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  phone: <><rect x="7" y="2.5" width="10" height="19" rx="2.5" /><path d="M11 18h2" /></>,
  laptop: <><rect x="4" y="5" width="16" height="11" rx="1.5" /><path d="M2 19h20" /></>,
  lock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
  passkey: <><path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" /><path d="M9 9.5v1M15 9.5v1M12 9.5v3.5h-1M9.5 16a4 4 0 0 0 5 0" /></>,
  spin: <path d="M21 12a9 9 0 1 1-6.2-8.6" />,
  down: <path d="M12 5v14M6 13l6 6 6-6" />,
  tray: <path d="M4 13h4l2 3h4l2-3h4M5.5 5h13l1.5 8v6H4v-6z" />,
  calc: <path d="M17 5H7l5 7-5 7h10" />,
  undo: <><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></>,
  download: <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />,
  sensor: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /></>,
  camera: <><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></>,
  upload: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M12 18v-6M9.5 14.5 12 12l2.5 2.5" /></>,
  flask: <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" />,
  cal: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  pulse: <path d="M3 12h4l2-4 3 8 2-4h7" />,
  chat: <path d="M4 5h16v11H9l-5 4z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  trash: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />,
  bin: <path d="M4 7h16M6 7l1 13h10l1-13" />,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 0 1 4.9.7c0 1.7-2.4 2.1-2.4 3.6M12 17h.01" /></>,
  chev: <path d="M9 5l7 7-7 7" />,
  logout: <path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10" />,
  link: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
  x: <path d="M6 6l12 12M18 6 6 18" />,
  minus: <path d="M5 12h14" />,
  ext: <path d="M14 4h6v6M20 4l-9 9M18 14v6H4V6h6" />,
}
export type IconName = keyof typeof P

export function Icon({ n, s, style }: { readonly n: IconName; readonly s?: 16 | 24; readonly style?: CSSProperties }) {
  return <svg class={s ? `i s${s}` : 'i'} viewBox="0 0 24 24" aria-hidden="true" style={style}>{P[n]}</svg>
}
export const Spinner = () => <Icon n="spin" s={16} style={{ animation: 'v-spin 1s linear infinite' }} />

export type Social = 'apple' | 'google' | 'facebook' | 'microsoft'
export function SocialIcon({ k, onDark }: { readonly k: Social; readonly onDark?: boolean }) {
  if (k === 'apple') return <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M16.37 12.6c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.76 2.28-1.6 2.78-.41 6.9 1.15 9.16.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.24-.02 2.02-1.12 2.77-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.66zM14.1 5.86c.63-.77 1.06-1.83.94-2.89-.91.04-2.01.61-2.66 1.37-.58.67-1.09 1.75-.96 2.79 1.02.08 2.05-.52 2.68-1.27z" /></svg>
  if (k === 'google') return <svg viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.87 2.68-6.62z" /><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18z" /><path fill="#FBBC05" d="M3.96 10.71A5.4 5.4 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3-2.33z" /><path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z" /></svg>
  if (k === 'facebook') return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="11" fill={onDark ? '#1877F2' : '#fff'} /><path fill={onDark ? '#fff' : '#1877F2'} d="M13.4 23v-8.1h2.4l.4-2.8h-2.8v-1.8c0-.8.2-1.35 1.4-1.35h1.45V6.4a19 19 0 0 0-2.1-.1c-2.1 0-3.5 1.27-3.5 3.6v2.2H8.3v2.8h2.4V23z" /></svg>
  return <svg viewBox="0 0 21 21" aria-hidden="true"><rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" /><rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" /></svg>
}

// ───────────── logo ─────────────

/** The Vivarango logo as inline SVG: a ring 70 % filled, clockwise from the top, and the word. */
export function Logo() {
  return (
    <svg class="v-brand-logo" viewBox="28.5 28.5 537.5 109" role="img" aria-label="Vivarango">
      <circle class="lt" cx="80" cy="80" r="43" fill="none" stroke-width="17" />
      <path class="la" d="M80.00 37.00 A43 43 0 1 1 39.10 93.29" fill="none" stroke-width="17" stroke-linecap="round" />
      <path class="lw" d={LOGO_WORD} />
    </svg>
  )
}
export function Brand({ variant }: { readonly variant?: 'lg' | 'stack' }) {
  const { t } = useApp()
  return <span class={`v-brand${variant ? ` v-brand--${variant}` : ''}`}><Logo /><span class="v-brand-by">{t.with} {INSURER}</span></span>
}

// ───────────── states ─────────────

export function Skeleton() {
  const { t } = useApp()
  return (
    <section class="v-stack" aria-busy="true" aria-label={t.loading} style={{ paddingTop: '8px' }}>
      <div class="v-skel" style={{ height: '16px', width: '30%' }} />
      <div class="v-skel" style={{ height: '32px', width: '65%' }} />
      <div class="v-card"><div class="v-stack"><div class="v-skel" style={{ height: '26px', width: '40%' }} /><div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}><div class="v-skel" style={{ width: '112px', height: '112px', borderRadius: '50%' }} /><div class="v-stack-s" style={{ flex: 1 }}><div class="v-skel" style={{ height: '52px', width: '70%' }} /><div class="v-skel" style={{ height: '14px', width: '50%' }} /></div></div><div class="v-skel" style={{ height: '10px' }} /><div class="v-skel" style={{ height: '14px', width: '80%' }} /></div></div>
      <div class="v-grid2"><div class="v-skel" style={{ height: '128px', borderRadius: '18px' }} /><div class="v-skel" style={{ height: '128px', borderRadius: '18px' }} /></div>
      <div class="v-skel" style={{ height: '150px', borderRadius: '18px' }} />
    </section>
  )
}

/** The calm error state; offline when the request never reached the server. */
export function ErrorState({ error, onRetry }: { readonly error: unknown; readonly onRetry: () => void }) {
  const { t } = useApp()
  const offline = error instanceof NetworkError
  return (
    <section class="v-screen v-stack-l" style={{ paddingTop: '24px', alignItems: 'center', textAlign: 'center' }}>
      <span class="v-ico v-ico--lg"><Icon n={offline ? 'offline' : 'retry'} /></span>
      <div class="v-stack-s"><h1 class="v-h1">{offline ? t.offTitle : t.errTitle}</h1><p class="v-lead">{offline ? t.offBody : t.errBody}</p></div>
      <button class="v-btn v-btn--primary" type="button" onClick={onRetry}>{t.retry}</button>
    </section>
  )
}

type Loaded<T> = { readonly data: T | null; readonly error: unknown; readonly loading: boolean; readonly reload: () => void }
/** Loads data for a screen. Keeps the last data while reloading, so an offline retry never blanks the screen. */
export function useLoad<T>(load: () => Promise<T>, deps: readonly unknown[] = []): Loaded<T> {
  const [state, setState] = useState<{ data: T | null; error: unknown; loading: boolean }>({ data: null, error: null, loading: true })
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    let live = true
    setState((s) => ({ ...s, loading: true }))
    load().then((data) => { if (live) setState({ data, error: null, loading: false }) }, (error: unknown) => { if (live) setState((s) => ({ data: s.data, error, loading: false })) })
    return () => { live = false }
  }, [attempt, ...deps])
  return { ...state, reload: () => setAttempt((n) => n + 1) }
}

// ───────────── sheets and toast ─────────────

/** A bottom sheet on the phone, a centered dialog on desktop. The page behind it is inert while it is open. */
export function Sheet({ label, onClose, children }: { readonly label: string; readonly onClose: (() => void) | null; readonly children: ComponentChildren }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null
    ref.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && onClose) onClose() }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); before?.focus() }
  }, [])
  return (
    <div class="v-ov" onClick={() => onClose?.()}>
      <div class="v-sheet" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={ref} onClick={(e) => e.stopPropagation()}>
        <div class="v-grab" />
        {children}
      </div>
    </div>
  )
}

export function Toast({ message }: { readonly message: string }) {
  return <div class="v-toast" role="status"><Icon n="check" />{message}</div>
}

/** The sheet shown for a social sign-in we cannot offer yet: it points to the code and the passkey. */
export function SocialSheet({ k, onCode, onPasskey }: { readonly k: Social; readonly onCode: () => void; readonly onPasskey: (() => void) | null }) {
  const app = useApp()
  const { t } = app
  const name = { apple: 'Apple', google: 'Google', facebook: 'Facebook', microsoft: 'Microsoft' }[k]
  const close = () => app.sheet(null)
  return (
    <Sheet label={fill(t.socTitle, { m: name })} onClose={close}>
      <div class="v-stack-l">
        <span class="v-ico v-ico--lg"><Icon n="info" /></span>
        <div class="v-stack-s"><h2 class="v-h1" style={{ fontSize: '26px' }}>{fill(t.socTitle, { m: name })}</h2><p class="v-body">{fill(t.socBody, { m: name })}</p></div>
        <div class="v-stack-s">
          <button class="v-btn v-btn--primary" type="button" onClick={() => { close(); onCode() }}><Icon n="mail" />{t.suEmail}</button>
          {onPasskey && <button class="v-btn v-btn--secondary" type="button" onClick={() => { close(); onPasskey() }}><Icon n="passkey" />{t.suPasskey}</button>}
          <button class="v-btn v-btn--quiet" type="button" onClick={close}>{t.cancel}</button>
        </div>
      </div>
    </Sheet>
  )
}

/** "Contactar": chat or email with the insurer's team. */
export function ContactSheet() {
  const app = useApp()
  const { t } = app
  const [step, setStep] = useState<'menu' | 'chat' | 'email'>('menu')
  const close = () => app.sheet(null)
  return (
    <Sheet label={t.ctTitle} onClose={close}>
      <div class="v-stack-l">
        <div class="v-stack-s"><h2 class="v-h1" style={{ fontSize: '26px' }}>{t.ctTitle}</h2><p class="v-body">{t.ctBody}</p></div>
        {step === 'menu' && (
          <div class="v-stack-s">
            <button class="v-choice" type="button" onClick={() => setStep('chat')} style={{ width: '100%', textAlign: 'left' }}><span class="v-ico v-ico--brand"><Icon n="chat" /></span><span class="v-choice-main"><span>{t.ctChat}</span><span class="v-choice-sub">{t.ctChatSub}</span></span><Icon n="chev" /></button>
            <button class="v-choice" type="button" onClick={() => setStep('email')} style={{ width: '100%', textAlign: 'left' }}><span class="v-ico v-ico--brand"><Icon n="mail" /></span><span class="v-choice-main"><span>{t.ctEmail}</span><span class="v-choice-sub">{t.ctEmailSub}</span></span><Icon n="chev" /></button>
          </div>
        )}
        {step === 'chat' && (
          <form class="v-stack" onSubmit={(e) => { e.preventDefault(); close(); app.toast(t.ctSent) }}>
            <div class="v-card v-card--flat v-stack-s">
              <span class="v-xs" style={{ fontWeight: 600 }}>{INSURER}</span>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '16px 16px 16px 4px', padding: '10px 14px', fontSize: '15px', alignSelf: 'flex-start', maxWidth: '90%' }}>{app.me ? fill(t.ctHello, { name: app.me.firstName }) : t.ctHelloAnon}</div>
            </div>
            <label class="v-field"><span class="sr">{t.ctType}</span><input class="v-input" placeholder={t.ctType} required /></label>
            <button class="v-btn v-btn--primary" type="submit">{t.ctSend}</button>
          </form>
        )}
        {step === 'email' && (
          <div class="v-stack">
            <div class="v-card v-card--flat v-stack-s"><span class="v-xs">{t.ctEmailL}</span><a class="v-h3" href={`mailto:${SUPPORT_EMAIL}`} style={{ overflowWrap: 'anywhere', color: 'var(--ink)' }}>{SUPPORT_EMAIL}</a></div>
            <button class="v-btn v-btn--primary" type="button" onClick={() => { void navigator.clipboard?.writeText(SUPPORT_EMAIL).catch(() => {}); close(); app.toast(t.ctCopied) }}>{t.ctCopy}</button>
          </div>
        )}
        <button class="v-btn v-btn--quiet" type="button" onClick={close}>{t.close}</button>
      </div>
    </Sheet>
  )
}

// ───────────── one-time code ─────────────

export type OtpState = 'idle' | 'checking' | 'ok' | 'wrong'
/** Six cells over one real input: paste and the phone's code autofill both work. */
export function Otp({ value, onChange, state, disabled, label }: { readonly value: string; readonly onChange: (v: string) => void; readonly state: OtpState; readonly disabled?: boolean; readonly label: string }) {
  const cls = state === 'ok' ? 'is-ok' : state === 'wrong' ? 'is-wrong' : state === 'checking' ? 'is-checking' : disabled ? 'is-off' : ''
  return (
    <label class={`v-otp ${cls}`}>
      <span class="sr">{label}</span>
      {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} class={`v-otp-cell${value[i] ? ' is-filled' : ''}${i === value.length ? ' is-next' : ''}`} aria-hidden="true">{value[i] ?? ''}</span>)}
      <input type="text" inputMode="numeric" autocomplete="one-time-code" pattern="[0-9]*" maxLength={6} autoFocus value={value} disabled={disabled || state === 'checking' || state === 'ok'}
        onInput={(e) => onChange(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))} />
    </label>
  )
}

/** The demo inbox: the message the app would have sent, polled every 2 seconds. Hidden when the server sends for real (404). */
export function Inbox({ path, onUse }: { readonly path: string; readonly onUse: (code: string) => void }) {
  const { t } = useApp()
  const [state, setState] = useState<{ on: boolean; msg: DemoMessage | null }>({ on: true, msg: null })
  useEffect(() => {
    let live = true
    let timer = 0
    const poll = async () => {
      try {
        const { messages } = await api<{ messages: readonly DemoMessage[] }>(path, { quiet401: true })
        const newest = [...messages].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null
        if (live) setState({ on: true, msg: newest })
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) { if (live) setState({ on: false, msg: null }); return }
      }
      if (live) timer = window.setTimeout(() => void poll(), 2000)
    }
    void poll()
    return () => { live = false; window.clearTimeout(timer) }
  }, [path])
  if (!state.on) return null
  const code = state.msg ? codeIn(state.msg.body) : null
  return (
    <section class="v-inbox" aria-label={t.inboxTitle} aria-live="polite">
      <div class="v-between"><h2 class="v-h3" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><Icon n="tray" s={16} />{t.inboxTitle}</h2></div>
      <p class="v-xs">{t.inboxNote}</p>
      {state.msg ? (
        <div class="v-inbox-msg">
          <span class="v-xs" style={{ fontWeight: 600 }}>{state.msg.subject}</span>
          <span class="v-xs">{fill(t.inboxTo, { to: state.msg.sentTo })}</span>
          <p class="v-inbox-body" style={{ marginTop: '4px' }}>{state.msg.body}</p>
        </div>
      ) : <p class="v-small" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><Spinner />{t.inboxWait}</p>}
      {code && <button class="v-btn v-btn--secondary v-btn--sm" type="button" style={{ alignSelf: 'flex-start' }} onClick={() => onUse(code)}>{t.inboxUse}</button>}
    </section>
  )
}

// ───────────── document crop ─────────────

const BOX = { w: 136, h: 68 }
/** A small view of one spot of the document: the image is scaled and moved so the spot fills the box, with some room around it. */
export function Crop({ src, spot, label, noSpot }: { readonly src: string | null; readonly spot: Spot | null; readonly label: string; readonly noSpot: string }) {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null)
  if (!src || !spot) return <div class="v-crop is-none" aria-hidden="true">{noSpot}</div>
  const pad = 1.25
  let img: CSSProperties = { visibility: 'hidden' }
  let mark: CSSProperties = { display: 'none' }
  if (size) {
    const sw = spot.width * size.w
    const sh = spot.height * size.h
    const scale = Math.min(BOX.w / (sw * pad), BOX.h / (sh * pad))
    const cx = (spot.x + spot.width / 2) * size.w
    const cy = (spot.y + spot.height / 2) * size.h
    const left = BOX.w / 2 - cx * scale
    const top = BOX.h / 2 - cy * scale
    img = { width: `${size.w * scale}px`, height: `${size.h * scale}px`, left: `${left}px`, top: `${top}px` }
    mark = { left: `${left + spot.x * size.w * scale - 2}px`, top: `${top + spot.y * size.h * scale - 2}px`, width: `${sw * scale + 4}px`, height: `${sh * scale + 4}px` }
  }
  return (
    <div class="v-crop" role="img" aria-label={label}>
      <img src={src} alt="" style={img} onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })} />
      <span class="v-crop-mark" style={mark} />
    </div>
  )
}
